import React, { forwardRef } from 'react'
import { useSelector } from 'react-redux'
import Text from 'particles/Text'
import { selectSummaryById } from 'state/campaigns'
import { useNewTranslation, useWindowSize } from 'hooks/index'
import TextProgress from './TextProgress'
import { DeadlineHeaderContainer } from './style'
import { selectRegistrationUsedSlots, selectRegistrationMaxSlots } from 'state/registrations'
import { selectSidebarStep } from 'state/review'
import Timeline from './ProgressTimeline/ProgressTimeline'
import { selectTimelineInfo } from 'state/review'
import RectangleButton from 'particles/Button/RectangleButton'
import Restricted from 'components/Shared/Permission/Restricted'
import { CampaignSubmission } from 'components/Shared/Permission/enums'
import IconButton from 'particles/Button/IconButton'
import DeadlineHeaderAlert from './DeadlineHeaderAlert'

interface DeadlineHeaderProps {
    campaignUuid: string
    isRegistrationMode: boolean
    isSingleTaskSectionMode: boolean
    isOverviewPageMode: boolean
    isAllSubmissionMode: boolean
    onExtendDeadline: () => void
    isDeadlineExtendable: boolean
}

const DeadlineHeader = forwardRef<HTMLDivElement, DeadlineHeaderProps>(
    (
        {
            campaignUuid,
            isRegistrationMode,
            isSingleTaskSectionMode,
            isOverviewPageMode,
            isAllSubmissionMode,
            onExtendDeadline,
            isDeadlineExtendable,
        },
        ref,
    ) => {
        const [newT] = useNewTranslation()
        const selectedStep = useSelector(selectSidebarStep)
        const campaignSummary = useSelector(selectSummaryById)[campaignUuid]
        const registrationUsedSlots = useSelector(selectRegistrationUsedSlots)
        const registrationMaxSlots = useSelector(selectRegistrationMaxSlots)

        const title = isRegistrationMode
            ? newT('submission.review.registrationTitle')
            : isSingleTaskSectionMode
              ? newT('submission.review.taskStepTitle', { stepTitle: selectedStep?.data.title })
              : isOverviewPageMode
                ? campaignSummary.title
                : newT('submission.review.allSubmissionsTitle')
        const completedSlot = isRegistrationMode
            ? newT('statusLabel.approvedCount', { count: registrationUsedSlots })
            : newT('statusLabel.completedCount', { count: campaignSummary?.total_finished_submission_count })
        const completedSlotCount = isRegistrationMode
            ? registrationUsedSlots
            : campaignSummary?.total_finished_submission_count
        const totalSlot = isRegistrationMode
            ? newT('statusLabel.registrationSlotCount', { count: registrationMaxSlots })
            : newT('statusLabel.campaignSlotCount', { count: Number(campaignSummary?.slot_number) })
        const totalSlotCount = isRegistrationMode ? registrationMaxSlots : campaignSummary?.slot_number

        const screenWidth = useWindowSize()
        const isTabletSize = screenWidth <= 768
        const isMobileSize = screenWidth <= 375

        const timelineInfo = useSelector(selectTimelineInfo(newT))

        return (
            <DeadlineHeaderContainer ref={ref}>
                <div className="container">
                    <div className="slot">
                        <Text scale={isMobileSize ? 'bodyS500' : 'bodyL500'} color="secondary">
                            {title}
                        </Text>
                        <div className="progress-timeline-container">
                            {typeof completedSlotCount === 'number' &&
                            typeof totalSlotCount === 'number' &&
                            totalSlotCount > 0 ? (
                                <TextProgress
                                    completedSlot={completedSlot}
                                    completedSlotTextType={isMobileSize ? 'caption700' : 'bodyM700'}
                                    totalSlot={totalSlot}
                                    totalSlotTextType={isMobileSize ? 'caption500' : 'bodyM500'}
                                />
                            ) : (
                                <div />
                            )}
                            <div className="timeline-container">
                                {isRegistrationMode && (
                                    <Timeline
                                        country={campaignSummary.country}
                                        title={newT('submission.review.registrationTimelineHeader')}
                                        progress={timelineInfo?.registrationProgress ?? 0}
                                        dotNumber={2}
                                        isMobileSize={isMobileSize}
                                        contentType={timelineInfo?.contentType}
                                        campaignDate={timelineInfo?.submitDate}
                                        reviewDate={{
                                            startDate: timelineInfo?.registrationDate?.reviewStartDate,
                                            endDate: timelineInfo?.registrationDate?.reviewEndDate,
                                            dayLeft: timelineInfo?.registrationDate?.reviewDayLeft,
                                        }}
                                    />
                                )}
                                {isSingleTaskSectionMode && (
                                    <Timeline
                                        country={campaignSummary.country}
                                        title={newT('submission.review.stepTimelineHeader')}
                                        progress={timelineInfo?.submissionProgress ?? 0}
                                        dotNumber={3}
                                        isMobileSize={isMobileSize}
                                        contentType={timelineInfo?.contentType}
                                        campaignDate={timelineInfo?.submitDate}
                                        reviewDate={timelineInfo?.submissionReviewDate}
                                        requestEditDate={timelineInfo?.requestEditDate}
                                        titleOnTopInFirstBreakPoint={isMobileSize || isTabletSize}
                                    />
                                )}
                                {isAllSubmissionMode && (
                                    <Timeline
                                        country={campaignSummary.country}
                                        title={newT('submission.review.submissionTimelineHeader')}
                                        progress={timelineInfo?.campaignOverallStepProgress ?? 0}
                                        dotNumber={2}
                                        isMobileSize={isMobileSize}
                                        contentType={timelineInfo?.contentType}
                                        campaignDate={timelineInfo?.submitDate}
                                        reviewDate={{
                                            startDate: timelineInfo?.submitDate.startDate,
                                        }}
                                    />
                                )}
                                {isOverviewPageMode && (
                                    <div className="progress-timeline-container">
                                        {campaignSummary.registration_enable ? (
                                            <>
                                                <Timeline
                                                    country={campaignSummary.country}
                                                    title={newT('submission.review.registrationTimelineHeader')}
                                                    progress={timelineInfo?.registrationProgress ?? 0}
                                                    dotNumber={2}
                                                    styleType={'short'}
                                                    titleOnTopInFirstBreakPoint={true}
                                                    isMobileSize={isMobileSize}
                                                    contentType={'registration'}
                                                    campaignDate={{
                                                        startDate: timelineInfo?.registrationDate?.reviewStartDate,
                                                        endDate: timelineInfo?.registrationDate?.reviewEndDate,
                                                    }}
                                                    reviewDate={{
                                                        startDate: timelineInfo?.registrationDate?.reviewStartDate,
                                                        endDate: timelineInfo?.registrationDate?.reviewEndDate,
                                                        dayLeft: timelineInfo?.registrationDate?.reviewDayLeft,
                                                    }}
                                                />
                                                <div className="divider" />
                                                <Timeline
                                                    country={campaignSummary.country}
                                                    title={newT('submission.review.submissionTimelineHeader')}
                                                    progress={timelineInfo?.campaignOverallStepProgress ?? 0}
                                                    dotNumber={2}
                                                    styleType={'short'}
                                                    titleOnTopInFirstBreakPoint={true}
                                                    isMobileSize={isMobileSize}
                                                    contentType={'allSubmission'}
                                                    campaignDate={timelineInfo?.submitDate}
                                                    reviewDate={{
                                                        startDate: timelineInfo?.submitDate.startDate,
                                                    }}
                                                />
                                            </>
                                        ) : (
                                            <Timeline
                                                country={campaignSummary.country}
                                                title={newT('submission.review.submissionTimelineHeader')}
                                                progress={timelineInfo?.campaignOverallStepProgress ?? 0}
                                                dotNumber={2}
                                                titleOnTopInFirstBreakPoint={true}
                                                isMobileSize={isMobileSize}
                                                contentType={'allSubmission'}
                                                campaignDate={timelineInfo?.submitDate}
                                                reviewDate={{
                                                    startDate: timelineInfo?.submitDate.startDate,
                                                }}
                                            />
                                        )}
                                    </div>
                                )}
                                {isDeadlineExtendable && !isRegistrationMode ? (
                                    <Restricted
                                        permissions={[
                                            CampaignSubmission.ReviewDirectly,
                                            CampaignSubmission.ReviewIndirectly,
                                        ]}
                                    >
                                        {isTabletSize ? (
                                            <IconButton
                                                icon="calendarPlus"
                                                size="xs"
                                                form="outlined"
                                                color="neutral"
                                                onClick={onExtendDeadline}
                                                className="extend-deadline-button"
                                            />
                                        ) : (
                                            <RectangleButton
                                                label={newT('submission.review.stepTimeLineExtendDeadlineButton')}
                                                size="s"
                                                color="neutral"
                                                form="outlined"
                                                onClick={onExtendDeadline}
                                                className="extend-deadline-button"
                                            />
                                        )}
                                    </Restricted>
                                ) : null}
                            </div>
                        </div>

                        <div className="reminder-container">
                            {timelineInfo?.reminderMessageKey &&
                                timelineInfo?.reminderMessageKey.map((text, index) => (
                                    <Text
                                        key={index}
                                        className="warning"
                                        color={timelineInfo?.reminderType}
                                        scale={isMobileSize ? 'bodyS700' : 'bodyM700'}
                                    >
                                        {text}
                                    </Text>
                                ))}
                        </div>
                        <Restricted permissions={[CampaignSubmission.ReviewCaptionWithAi]}>
                            <div>
                                <DeadlineHeaderAlert campaignUuid={campaignUuid} />
                            </div>
                        </Restricted>
                    </div>
                </div>
            </DeadlineHeaderContainer>
        )
    },
)

DeadlineHeader.displayName = 'DeadlineHeader'

export default DeadlineHeader
